<script lang="ts" setup>
import {computed, ref} from 'vue';
import {captureException, captureMessage} from "@sentry/browser";
import QuizButton from '@/components/Quiz/QuizButton.vue';
import ResendVerificationCode from '@/components/Quiz/ResendVerificationCode.vue';
import {useQuizStore} from '@/stores/quizStore';
import { changePhone, creditReport } from '@/services/quizService.ts'
import QuizSecureTag from "@/components/Quiz/QuizSecureTag.vue";
import IntlTelInput from 'intl-tel-input/vueWithUtils'
import 'intl-tel-input/build/css/intlTelInput.css'
import { countries, countryCodes, defaultCountry } from '@/helpers/constants.ts'
import {type CountryCode, isValidPhoneNumber } from 'libphonenumber-js'

const {
  token,
  isFetching,
  setIsFetching,
  goToNextStep,
  showAlert,
  setAgreementsCount,
  setClientPortalUrl,
  contact,
} = useQuizStore()

const verificationCode = ref('')
const isValid = computed(() => verificationCode.value.length === 6)
const wantChangeThePhone = ref(false)

const phone = ref<string>('')
const phoneIsValid = ref(false)
const showErrors = ref(false)

const initialCountry = !contact.phone
  ? defaultCountry
  : (Object.keys(countryCodes).find(country => contact.phone.startsWith(countryCodes[country as keyof typeof countryCodes])) || defaultCountry)

const selectedCountry = ref(initialCountry)

function handleChangeNumber(value: string) {
  phone.value = value
  phoneIsValid.value = isValidPhoneNumber(value, selectedCountry.value.toUpperCase() as CountryCode)
}

function handleChangeCountry(country: string) {
  selectedCountry.value = country
}

const handleSubmit = () => {
  if (!isValid.value) return

  setIsFetching(true)
  creditReport(token.value, verificationCode.value)
    .then((response) => {
      setAgreementsCount(response.agreements_count)
      setClientPortalUrl(response.client_portal_url)
      setIsFetching(false)
      goToNextStep()
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on creditReport: CreditReportAuthentication.vue");
        captureException(err);
      }
    })
    .finally(() => {
      setIsFetching(false)
    })
}

const handleSubmitChangePhone = () => {
  if (!phoneIsValid.value) {
    showErrors.value = true
    return
  }

  setIsFetching(true)
  changePhone(token.value, phone.value)
    .then((response) => {
      showAlert(response.message, 'success')
    })
    .catch(async (err) => {
      const response = await err.response?.json()
      if (response?.error) {
        showAlert(response.error)
      } else {
        captureMessage("Error on changePhone: CreditReportAuthentication.vue");
        captureException(err);
      }
    })
    .finally(() => {
      setIsFetching(false)
      wantChangeThePhone.value = false
    })
}

const handleWantChangePhone = () => {
  phone.value = ''
  verificationCode.value = ''
  wantChangeThePhone.value = true
}
</script>

<template>
  <img src="@/assets/icons/secure-icon.webp" alt="trophy" class="h-22 mx-auto py-2"/>

  <h2 class="text-xl font-bold text-center mb-4">
    <span class="text-(--brand-primary) ">Quick Security Check</span><br>
    You're Almost Finished!
  </h2>

  <p class="text-gray-600 mb-4 font-medium">
    Check your mobile, we've sent your code!<br>Enter it below to unlock your results.
  </p>

  <div v-if="!wantChangeThePhone">
    <div class="mb-4">
      <label for="code" class="block text-gray-600 font-medium mb-2">Enter 6-digit verification
        code</label>
      <input id="code" type="text" v-model="verificationCode"
             class="w-full py-3 px-4 border border-gray-300 rounded-lg focus:outline-none focus:border-amber-500">
    </div>

    <ResendVerificationCode/>

    <QuizButton label="Verify Code" :isFetching="isFetching" :disabled="!isValid || isFetching"
                @click="handleSubmit" id="verify-code-button"/>

    <div class="mt-4 text-center">
      <button :disabled="isFetching"
              class="text-blue-600 cursor-pointer hover:text-blue-800 disabled:text-gray-400 disabled:cursor-not-allowed"
              @click="handleWantChangePhone">
        Want to change the phone number?
      </button>
    </div>
  </div>

  <div v-if="wantChangeThePhone">
    <div class="flex flex-col justify-end mb-4"
         :class="showErrors && !phoneIsValid ? '*:border-red-500': ''">
      <label for="phone" class="block text-gray-700 font-medium mb-1">Phone*</label>
      <IntlTelInput ref="phoneRef" :options="{
        initialCountry: initialCountry,
        onlyCountries: countries,
        containerClass: 'w-full flex border border-gray-300 rounded-lg px-3'
      }" :inputProps="{
        id: 'phone',
        placeholder: '7400 123456',
        class: 'grow p-2 focus:outline-none',
        nationalMode: false,
      }" :value="phone" @changeNumber="handleChangeNumber"
                    @changeCountry="handleChangeCountry"/>
    </div>

    <QuizButton label="Change the phone" :isFetching="isFetching" :disabled="isFetching"
                @click="handleSubmitChangePhone" id="change-phone-button"/>
  </div>
  <QuizSecureTag class="mt-6"/>
</template>
